/**
 * Shared transport for Remult / entity hooks that cannot inject NotificationsService.
 * Nest routes and jobs should use NotificationsService directly.
 *
 * Active exports: sendMessageToRecipients, sendPushToUsers (used by shared code).
 * Legacy exports: sendMessageWithPush, sendNotificationWithPush (unused – do not use).
 */

import {
  DEFAULT_NOTIFICATION_TEMPLATES,
  replaceTemplatePlaceholders,
} from "@config/notification-templates.config";
import type { Notification } from "@config/push-notifications";
import { PushChannels, PushGroups } from "@config/push-notifications";
import { userRepo } from "@repositories";
import { WEME_APP_BASE_URL } from "@shared/helpers/constants";
import type {
  FallbackOptions,
  MessageType,
  Recipient,
  Transport,
} from "@shared/types/message.types";
import {
  sendEmail,
  sendNotificationSingleBatch,
  sendSms,
  sendWhatsapp,
  type SmsRecipientResult,
} from "server/src/notifications/messaging.function";

const FCM_BATCH_SIZE = 500;

export type MessagePayload = {
  message: string;
  senderName?: string;
  includedUrl?: string;
  sendInDev?: boolean;
  isOtp?: boolean;
  preventMessageWrap?: boolean;
  title?: string;
  body?: string;
  channelId?: Notification["channelId"];
  group?: Notification["group"];
  actions?: Notification["actions"];
  mainAction?: Notification["mainAction"];
};

export type SendMessageToRecipientsResultItem = {
  recipient: Recipient;
  success: boolean;
  method: MessageType;
  deliveryUncertain?: boolean;
};

export type SendMessageWithPushResultItem = {
  recipientId: string;
  success: boolean;
  method: Transport;
};

function fillNotificationDefaults(
  notification: Partial<Notification>,
): Notification {
  return {
    title: notification.title ?? "",
    body: notification.body ?? "",
    channelId: notification.channelId ?? PushChannels.Main,
    group: notification.group ?? PushGroups.DEFAULT,
    ongoing: notification.ongoing ?? false,
    actions: notification.actions,
    mainAction: notification.mainAction,
  };
}

/**
 * Sends a message to users by ID with push first, then SMS/WhatsApp/email fallback.
 * Use when you have user IDs. For recipients without IDs use sendMessageToRecipients.
 * @deprecated Temporary facade. Prefer NotificationsService.sendMessageWithPush in server code.
 */
export async function sendMessageWithPush(
  message: MessagePayload,
  recipientUserIds: string[],
): Promise<SendMessageWithPushResultItem[]> {
  if (recipientUserIds.length === 0) return [];
  const users = await userRepo.find({
    where: { id: { $in: recipientUserIds } },
  });
  const idToUser = new Map(users.map((u) => [u.id, u]));
  const ordered = recipientUserIds
    .map((id) => idToUser.get(id))
    .filter((u): u is (typeof users)[0] => !!u);
  const senderName = message.senderName ?? "מערכת Weme";
  const sendInDev = message.sendInDev ?? false;

  const results: SendMessageWithPushResultItem[] = ordered.map((u) => ({
    recipientId: u.id,
    success: false,
    method: "push",
  }));
  let pending = ordered.map((u, i) => ({ user: u, resultIndex: i }));

  // Push first
  const withTokens = pending.filter((p) => p.user.fcmToken);
  if (withTokens.length > 0) {
    const pushUserIds = withTokens.map((p) => p.user.id);
    const notification = fillNotificationDefaults({
      title: message.title ?? message.message.slice(0, 50),
      body: message.body ?? message.message,
      channelId: message.channelId,
      group: message.group,
      actions: message.actions,
      mainAction: message.mainAction,
    });
    const pushResult = await sendPushToUsers(notification, pushUserIds, {
      sendInDev,
    });
    const pushSuccessList = Array.isArray(pushResult)
      ? pushResult
      : [pushResult];
    withTokens.forEach((p, idx) => {
      const success = pushSuccessList[idx] ?? false;
      if (success) {
        results[p.resultIndex] = {
          recipientId: p.user.id,
          success: true,
          method: "push",
        };
      }
    });
    const pushSucceeded = new Set(
      withTokens.filter((_, idx) => pushSuccessList[idx]).map((p) => p.user.id),
    );
    pending = pending.filter((p) => !pushSucceeded.has(p.user.id));
  }

  // Then SMS, WhatsApp, email for remaining
  const fallbackOrder: MessageType[] = ["sms", "whatsapp", "email"];
  for (const order of fallbackOrder) {
    if (pending.length === 0) break;
    const pendingWithContact = pending.filter((p) =>
      order === "email" ? p.user.email : p.user.phoneNumber,
    );
    const flattened = pendingWithContact.map((p) =>
      order === "email" ? p.user.email! : p.user.phoneNumber!,
    );
    if (flattened.length === 0) continue;

    const text =
      order === "email"
        ? `התקבלה הודעה חדשה מ${senderName}:\n\n${message.message}\n\n${message.includedUrl ? `מעבר למערכת: ${WEME_APP_BASE_URL}${message.includedUrl.startsWith("/") ? "" : "/"}${message.includedUrl}\n\n` : ""}`
        : message.message;

    let methodResults: { recipient: string; success: boolean }[];
    if (order === "sms") {
      methodResults = await sendSms({
        message: text,
        recipients: flattened,
        isOtp: message.isOtp,
        sendInDev,
      });
    } else if (order === "email") {
      methodResults = await sendEmail({
        message: text,
        recipients: flattened,
        sendInDev,
      });
    } else {
      methodResults = await sendWhatsapp({
        message: text,
        recipients: flattened,
        senderName,
        includedUrl: message.includedUrl,
        sendInDev,
      });
    }

    const succeeded = new Set(
      methodResults
        .map((r, i) => (r.success ? pendingWithContact[i] : null))
        .filter(Boolean) as typeof pendingWithContact,
    );
    pending = pending.filter((p) => {
      const ok = succeeded.has(p);
      if (ok) {
        results[p.resultIndex] = {
          recipientId: p.user.id,
          success: true,
          method: order,
        };
      }
      return !ok;
    });
  }

  return results;
}

/**
 * Sends a templated notification to users by ID (push first, then SMS fallback).
 * Use when you have user IDs. For phone-only use sendMessageToRecipients with template message.
 * @deprecated Temporary facade. Prefer NotificationsService.sendNotificationWithPush in server code.
 */
export async function sendNotificationWithPush(
  templateKey: keyof typeof DEFAULT_NOTIFICATION_TEMPLATES,
  userIds: string[],
  placeholders: Record<string, string> = {},
): Promise<SendMessageWithPushResultItem[]> {
  if (userIds.length === 0) return [];
  const template = DEFAULT_NOTIFICATION_TEMPLATES[templateKey];
  const message = replaceTemplatePlaceholders(template, placeholders);
  return sendMessageWithPush(
    { message, title: "Weme", body: message },
    userIds,
  );
}

/**
 * Sends a message to recipients by contact (no user ID). No push.
 * Used by Remult entity hooks and templated-user-notification.
 */
export async function sendMessageToRecipients(
  message: MessagePayload,
  recipients: Recipient[],
  fallbackOrder: FallbackOptions,
): Promise<SendMessageToRecipientsResultItem[]> {
  const senderName = message.senderName ?? "מערכת Weme";
  const sendInDev = message.sendInDev ?? false;
  const ret: SendMessageToRecipientsResultItem[] = recipients.map((r) => ({
    recipient: r,
    success: false,
    method: fallbackOrder[fallbackOrder.length - 1],
  }));
  let pending = recipients.map((r, i) => ({ recipient: r, i }));

  for (const order of fallbackOrder) {
    if (pending.length === 0) break;
    const flattened = pending.map((p) =>
      order === "email" ? p.recipient.email : p.recipient.phoneNumber,
    );
    const text =
      order === "email"
        ? `התקבלה הודעה חדשה מ${senderName}:\n\n${message.message}\n\n${message.includedUrl ? `מעבר למערכת: ${WEME_APP_BASE_URL}${message.includedUrl.startsWith("/") ? "" : "/"}${message.includedUrl}\n\n` : ""}`
        : message.message;

    let methodResults: SmsRecipientResult[];
    if (order === "sms") {
      methodResults = await sendSms({
        message: text,
        recipients: flattened,
        isOtp: message.isOtp,
        sendInDev,
      });
    } else if (order === "email") {
      methodResults = await sendEmail({
        message: text,
        recipients: flattened,
        sendInDev,
      });
    } else {
      methodResults = await sendWhatsapp({
        message: text,
        recipients: flattened,
        senderName,
        includedUrl: message.includedUrl,
        sendInDev,
      });
    }

    pending = pending.filter((p, idx) => {
      const methodResult = methodResults[idx];
      const success = methodResult?.success ?? false;
      if (success) {
        ret[p.i] = { recipient: p.recipient, success: true, method: order };
      } else if (methodResult?.deliveryUncertain) {
        ret[p.i] = {
          recipient: p.recipient,
          success: false,
          method: order,
          deliveryUncertain: true,
        };
      }
      return !success;
    });
  }
  return ret;
}

/**
 * Sends push notification to user IDs (resolves FCM tokens via Remult userRepo).
 * Used by templated-user-notification.
 */
export async function sendPushToUsers(
  notification: Partial<Notification>,
  userIds: string[],
  options?: { sendInDev?: boolean },
): Promise<boolean[] | boolean> {
  if (userIds.length === 0) return [];
  const users = await userRepo.find({
    where: {
      id: {
        $in: userIds,
      },
    },
  });
  const idToToken = new Map(users.map((u) => [u.id, u.fcmToken]));
  const orderedTokens = userIds.map((id) => idToToken.get(id) ?? null);
  const validTokens = orderedTokens.filter((t): t is string => !!t);
  if (validTokens.length === 0) {
    return userIds.length === 1 ? false : userIds.map(() => false);
  }
  const filled = fillNotificationDefaults(notification);
  const chunks: string[][] = [];
  for (let i = 0; i < validTokens.length; i += FCM_BATCH_SIZE) {
    chunks.push(validTokens.slice(i, i + FCM_BATCH_SIZE));
  }
  const sendInDev = options?.sendInDev ?? false;
  const batchResults = await Promise.all(
    chunks.map((chunk) =>
      sendNotificationSingleBatch(chunk, filled, sendInDev),
    ),
  );
  const results = batchResults.flat();
  const successByToken = new Map(results.map((r) => [r.recipient, r.success]));
  const successMap = orderedTokens.map((t) =>
    t ? (successByToken.get(t) ?? false) : false,
  );
  return userIds.length === 1 ? successMap[0]! : successMap;
}
